import { INameCode } from '@/interfaces/entities/name-code.interface';
import { RequestServices as _http } from '../common/request.services';
import {
  getPersonTitles as getPersonTitlesApi,
  getGenders as getGendersApi,
  getPetBreeds as getPetBreedsApi,
  getPetTypes as getPetTypesApi,
  fetchHomeClaimTypes,
  fetchHomeLossTypes,
  fetchHomeLossValues,
  getHomeAdvancedCover,
  getHomeFloorOccupancy,
  getHomeGroupCoverPriceBands,
  getSmallBusinessInsuranceLimitValue,
  getEarthquakeExcesses,
  getPortableExcesses,
  getPetExcesses,
  getCombinedAccessoriesList,
  getRoofTypeMaterials,
  getPrimaryConstructionMaterials,
  getHomeOccupancyTypes,
  getBuildingTypes,
  getPortableMobileExcesses,
  getAtFaultClaimTypes,
  getAtFaultClaimValues,
  getDriverLicenseIssueTypes,
  getFrameMaterials,
  getFloorMaterials,
  getMonitoredAlarmTypes,
  getInsuranceProducts,
  getHomeAndContentProducts,
  getHomeAndContentBuildingPropertyFeatures,
  getMembershipLevels,
  getNumOfStoreys,
  getLowestOccupiedFloor,
  getQuotingFeesPayByMonth,
  getParkingLocationsUrl,
  getVehicleBusinessUseTypesListUrl,
  getFinanceTypesUrl,
  getDriverLicenseYearsHeldRangesUrl,
  getPortableItemCategories,
  getHomeItemCategories,
  getHomeExcessValues,
  getContentsExcessValues,
  getAdvancedCoverValues,
  getPetCover,
  getDriverRatingsUrl,
  getVehicleAgreedValueRangeUrl,
  getPetExcessOptions,
  getYearsRatingOneHeldUrl,
  getCancellationHistoryUrl,
  getBusinessUseWarningEnable,
  getSuburbsEmbargo,
  getHomeAndContentsGroupCoverPriceBands,
} from '@/constants/forms-api.const';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { GroupedQuoteType, IMembershipLevel } from '@/interfaces';
import { IHomeExcessValueReferenceData } from '@/interfaces/entities/home-contents.interface';
import { APIValues } from '@/constants/home-quote.const';

export const ReferenceDataServices = {
  getPersonTitles() {
    return _http.HttpGet(getPersonTitlesApi);
  },

  getGenders() {
    return _http.HttpGet(getGendersApi);
  },

  getPetTypes(quoteType: string) {
    return _http.HttpGet(getPetTypesApi.replace('{0}', quoteType));
  },

  getPetBreeds(quoteType: string, petType: string): Promise<INameValue[]> {
    return _http.HttpGet(getPetBreedsApi.replace('{0}', quoteType).replace('{1}', petType));
  },

  getInsuranceProducts() {
    return _http.HttpGet(getInsuranceProducts);
  },

  getHomeAndContentProducts(occupancyType: string, buildingType: string): Promise<INameValue[]> {
    return _http.HttpGet(getHomeAndContentProducts.replace('{0}', occupancyType).replace('{1}', buildingType));
  },
  getHomeAndContentBuildingPropertyFeatures() {
    return _http.HttpGet(getHomeAndContentBuildingPropertyFeatures);
  },
  getHomeAndContentsGroupCoverPriceBands(quoteType: string) {
    return _http.HttpGet(getHomeAndContentsGroupCoverPriceBands.replace('{0}', quoteType));
  },

  getSuburbsEmbargo(
    postCode: string,
    suburb: string,
    gnaf: string,
    state: string,
    policyLine: string,
    vehicleType: string,
    jobType: string,
    coveragePatternCode: string,
    effectiveDate: string,
    writtenDate: string
  ) {
    return _http.HttpGet(
      getSuburbsEmbargo
        .replace('{0}', postCode)
        .replace('{1}', suburb)
        .replace('{2}', gnaf)
        .replace('{3}', state)
        .replace('{4}', policyLine)
        .replace('{5}', vehicleType)
        .replace('{6}', jobType)
        .replace('{7}', coveragePatternCode)
        .replace('{8}', effectiveDate)
        .replace('{9}', writtenDate)
    );
  },

  //#region HOME AND CONTENT
  getHomeClaimTypes(quoteType: string) {
    return _http.HttpGet(fetchHomeClaimTypes.replace('{0}', quoteType));
  },

  getHomeLossTypes(quoteType: string) {
    return _http.HttpGet(fetchHomeLossTypes.replace('{0}', quoteType));
  },

  getHomeLossValues(quoteType: string) {
    return _http.HttpGet(fetchHomeLossValues.replace('{0}', quoteType));
  },

  getHomeAdvancedCover(quoteType: string, occupancyType: string) {
    return _http.HttpGet(getHomeAdvancedCover.replace('{0}', quoteType).replace('{1}', occupancyType));
  },

  getHomeRoofTypeMaterials(quoteType: string) {
    return _http.HttpGet(getRoofTypeMaterials.replace('{0}', quoteType));
  },

  getHomePrimaryConstructionMaterials(quoteType: string) {
    return _http.HttpGet(getPrimaryConstructionMaterials.replace('{0}', quoteType));
  },

  getHomeOccupancyTypes(): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getHomeOccupancyTypes);
  },

  getNumOfStoreys(buildingType: string): Promise<INameValue[]> {
    return _http.HttpGet(getNumOfStoreys.replace('{0}', buildingType));
  },

  getLowestOccupiedFloor(buildingType: string) {
    return _http.HttpGet(getLowestOccupiedFloor.replace('{0}', buildingType));
  },

  getBuildingTypes(): Promise<INameValue[]> {
    return _http.HttpGet(getBuildingTypes);
  },

  getHomeFloorOccupancy(buildingType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getHomeFloorOccupancy.replace('{0}', buildingType));
  },

  getHomeGroupCoverPriceBands(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getHomeGroupCoverPriceBands.replace('{0}', quoteType));
  },

  getPortableItemCategories(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPortableItemCategories.replace('{0}', quoteType));
  },

  getHomeItemCategories(quoteType: string, withPolicyUpdate: boolean): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getHomeItemCategories.replace('{0}', quoteType).replace('{1}', withPolicyUpdate.toString()));
  },

  getSmallBusinessInsuranceLimitValue(quoteType: string) {
    return _http.HttpGet(getSmallBusinessInsuranceLimitValue.replace('{0}', quoteType));
  },

  getEarthquakeExcesses(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getEarthquakeExcesses.replace('{0}', quoteType));
  },

  getPetExcesses(quoteType: string, occupancyType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPetExcesses.replace('{0}', quoteType).replace('{1}', occupancyType));
  },

  getPetExcessOptions(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPetExcessOptions.replace('{0}', quoteType));
  },

  getPortableExcesses(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPortableExcesses.replace('{0}', quoteType));
  },

  getPortableMobileExcesses(quoteType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPortableMobileExcesses.replace('{0}', quoteType));
  },

  getFrameMaterials(quoteType: string) {
    return _http.HttpGet(getFrameMaterials.replace('{0}', quoteType));
  },

  getFloorMaterials(quoteType: string) {
    return _http.HttpGet(getFloorMaterials.replace('{0}', quoteType));
  },

  getMembershipLevels(quoteType: string): Promise<{ values: IMembershipLevel[] }> {
    return _http.HttpGet(getMembershipLevels.replace('{0}', quoteType));
  },

  getMonitoredAlarmTypes(quoteType: string): Promise<INameValue[]> {
    return _http.HttpGet(getMonitoredAlarmTypes.replace('{0}', quoteType));
  },

  getHomeExcessValues(): Promise<{ values: IHomeExcessValueReferenceData[] }> {
    return _http.HttpGet(getHomeExcessValues);
  },

  getContentsExcessValues(): Promise<{ values: IHomeExcessValueReferenceData[] }> {
    return _http.HttpGet(getContentsExcessValues);
  },

  getAdvancedCoverValues(quoteType: string, occupancyType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getAdvancedCoverValues.replace('{0}', quoteType).replace('{1}', occupancyType));
  },

  getPetCover(quoteType: string, occupancyType: string): Promise<{ values: INameCode[] }> {
    return _http.HttpGet(getPetCover.replace('{0}', quoteType).replace('{1}', occupancyType));
  },

  //#endregion

  //#region MOTOR
  getCombinedAccessoriesList() {
    return _http.HttpGet(getCombinedAccessoriesList);
  },

  getAtFaultClaimTypes() {
    return _http.HttpGet(getAtFaultClaimTypes);
  },

  getAtFaultClaimValues() {
    return _http.HttpGet(getAtFaultClaimValues);
  },

  getLicenceCancellationTypes() {
    return _http.HttpGet(getDriverLicenseIssueTypes);
  },

  getLicenceCancellationHistories() {
    return _http.HttpGet(getCancellationHistoryUrl);
  },

  getQuotingFeesPayByMonth(effectiveDate: Date) {
    return _http.HttpGet(getQuotingFeesPayByMonth.replace('{0}', effectiveDate.toDateString()));
  },

  getParkingLocations() {
    return _http.HttpGet(getParkingLocationsUrl);
  },

  getBusinessUseTypes() {
    return _http.HttpGet(getVehicleBusinessUseTypesListUrl);
  },

  getFinanceTypes() {
    return _http.HttpGet(getFinanceTypesUrl);
  },

  getDriverRatings() {
    return _http.HttpGet(getDriverRatingsUrl);
  },

  getDriverLicenseYearsHeldRanges() {
    return _http.HttpGet(getDriverLicenseYearsHeldRangesUrl);
  },

  getYearsRatingOneHeld() {
    return _http.HttpGet(getYearsRatingOneHeldUrl);
  },

  getVehicleAgreedValueRange(
    quoteType: string,
    marketValue: number
  ): Promise<{
    minValue: number;
    maxValue: number;
    leastMaximumValueCap: number;
  }> {
    return _http.HttpGet(
      getVehicleAgreedValueRangeUrl.replace('{0}', quoteType).replace('{1}', marketValue.toString())
    );
  },

  getBusinessUseWarningEnable(policyStartDate: Date): Promise<boolean> {
    return _http.HttpGet(getBusinessUseWarningEnable.replace('{0}', policyStartDate.toDateString()));
  },

  //#endregion
};
